import pkgText from "../../package.json?raw";
import { gameStore } from "./store/openStore";
import { saveStore } from "./save/openSaveStore";
import { currentPlatform } from "./platform/shell";
import { webDevice } from "./adapters";
import type { CopyDiagnostics } from "../features/contracts";
import { DiagEntry, diagLog } from "./diagLog";
import { JournalLine, frameJournal, journalLineText } from "./frameJournal";
import { CrashRecord, Crumb, breadcrumbs, crashLines } from "./breadcrumbs";
import { glContexts } from "./glContexts";

export { installDiagnostics, recordDiag } from "./diagLog";

/**
 * Local diagnostics (FINISH_PLAN WP4.4, X14): the last warnings and errors kept in a ring buffer in memory, and
 * Settings → Copy diagnostics, which hands them with the build, the device and the save's health to the share sheet
 * or the clipboard. Nothing is sent anywhere by the game (there is no server, ADR 0012): the player chooses to pass
 * the text on, the way Settings → Save backup works.
 */

/** What the report says, gathered by `gatherFacts` in the browser and passed in by tests. */
export interface DiagFacts {
  readonly version: string;
  readonly build: string;
  readonly platform: string;
  readonly userAgent: string;
  readonly screen: string;
  readonly standalone: boolean;
  readonly storage: string;
  readonly store: string;
  readonly saveProblems: readonly string[];
  readonly nowMs: number;
}

/** The report's text. No player id, no save contents: only what helps find a bug. */
export function diagnosticsText(f: DiagFacts, entries: readonly DiagEntry[], frames: readonly JournalLine[] = [], forensics: readonly string[] = []): string {
  const time = (ms: number) => new Date(ms).toISOString().replace("T", " ").slice(0, 19);
  const lines = [
    `Highmarch diagnostics · ${time(f.nowMs)} UTC`,
    `Build: ${f.version} · ${f.build}`,
    `Platform: ${f.platform}${f.standalone ? " · home-screen app" : ""}`,
    `Device: ${f.userAgent}`,
    `Screen: ${f.screen}`,
    `Save: ${f.storage}`,
    `Store: ${f.store}`,
    `Save problems: ${f.saveProblems.length === 0 ? "none" : ""}`,
    ...f.saveProblems.map((p) => `  - ${p}`),
    ...forensics,
    `Log (last ${entries.length}):`,
    ...entries.map((e) => `  ${time(e.atMs).slice(11)} ${e.level.toUpperCase().padEnd(7)} ${e.message}`),
    ...(frames.length > 0 ? [`Frame rate (battle, 30 s windows; last ${frames.length}):`, ...frames.map(journalLineText)] : []),
  ];
  return lines.join("\n");
}

async function gatherFacts(): Promise<DiagFacts> {
  const store = saveStore();
  let quota = "";
  try {
    const est = await navigator.storage?.estimate?.();
    const persisted = await navigator.storage?.persisted?.();
    const Mb = 1024 * 1024;
    if (est?.usage !== undefined && est.quota !== undefined) quota = ` · origin ${(est.usage / Mb).toFixed(1)} of ${(est.quota / Mb).toFixed(0)} MB${persisted ? " (persistent)" : ""}`;
  } catch { /* estimate() is missing in older WebKit */ }
  const p = currentPlatform();
  return {
    version: `v${(JSON.parse(pkgText) as { version: string }).version}`,
    build: typeof __BUILD_STAMP__ === "string" ? `${__BUILD_STAMP__} UTC` : "dev",
    platform: `${p.name} (haptics ${p.supportsHaptics ? "on" : "off"}, local notifications ${p.supportsLocalNotifications ? "on" : "off"}, IAP ${p.supportsIap ? "on" : "off"})`,
    userAgent: navigator.userAgent,
    screen: `${screen.width}×${screen.height} @${devicePixelRatio}x, viewport ${innerWidth}×${innerHeight}`,
    standalone: webDevice.isStandalone(),
    storage: store ? `${store.backendName} + localStorage mirror, ${store.sizeOf("save")} characters${quota}` : `not opened${quota}`,
    store: gameStore()?.kind ?? "none (gem packs come with the iOS app)",
    saveProblems: store?.problems.map((x) => x.message) ?? [],
    nowMs: Date.now(),
  };
}

/** Settings → Copy diagnostics (the contract in features/contracts.ts). */
export const copyDiagnostics: CopyDiagnostics = async () => {
  const text = diagnosticsText(await gatherFacts(), diagLog.entries(), frameJournal.entries(), forensicsLines());
  return webDevice.shareText("Highmarch diagnostics", text);
};

/**
 * M33: the sessions iOS closed (infra/breadcrumbs.ts), the WebGL contexts alive now, and the breadcrumb as it stands.
 * Pure over what it is given, for the tests.
 */
export function forensicsText(crashes: readonly CrashRecord[], contexts: readonly string[], now: Crumb | null): string[] {
  return [
    `Closed by iOS: ${crashes.length === 0 ? "never (as far as this device has recorded)" : `${crashes.length} time${crashes.length === 1 ? "" : "s"} recorded`}`,
    ...crashes.slice().reverse().flatMap(crashLines),
    `WebGL contexts now: ${contexts.length > 0 ? contexts.join(", ") : "none"}`,
    ...(now ? [`Now: ${now.screen}${now.level !== null ? ` L${now.level}` : ""}${now.stage !== null ? ` stage ${now.stage}` : ""} · up ${Math.round((now.at - now.startedAt) / 1000)} s${now.lastLoss ? ` · last context loss ${now.lastLoss}` : ""}`] : []),
  ];
}

function forensicsLines(): string[] {
  return forensicsText(breadcrumbs.crashes(), glContexts.labels(), breadcrumbs.current());
}
